// Storage of the team-adoption record - see doc/team-dashboard-concept.org §5.
//
// Unlike Contract/Week/Focus, none of this is daily-cadence data: a change-track
// line, a quick win, a review-gate entry or a pulse reading can land on any day,
// and the "current" state of the change track is one evolving object rather than
// a per-day record. So it all lives in *one* persistent file, the way gtdPath
// does, and not in the daily-note window - a new `teamAdoptionPath` setting,
// default /roam/team.org.
//
// Written as real Org headlines and plain lists, never a properties drawer, so
// the record is a first-class Org artifact any other tool (agenda, mobile
// client, Emacs) reads without knowing anything about organice:
//
//   * Team :team-adoption:
//   ** TODO The reason this matters, said out loud :reason:
//   Said it in Monday's standup, no slides, no deadline attached.
//   ** TODO A guiding group, not the lead alone :coalition:active:
//   ** DONE Anchored in how the team normally works :anchoring:
//   ** Quick wins
//   - [2026-08-04 Tue] Release notes drafted by AI, corrected in review, shipped same day
//   ** Review gate
//   - [2026-08-04 Tue] Release-notes generator
//     - verified :: ran it over last month's log and diffed the output by hand
//     - decision :: group
//   ** Pulse
//   - [2026-08-04 Tue] 4.2
//
// Two conventions worth stating, since both are load-bearing on read:
//
//   - Each change-track line is identified by its *tag* (`:reason:`,
//     `:coalition:`, ...), never by its title - so the labels in lib/team.js
//     stay copy that can be reworded without resetting anybody's state.
//   - A line's state is tri-state but Org keywords here are only TODO/DONE:
//     DONE means /anchored/, and the middle state /active/ is carried by an
//     extra `:active:` tag. Both are ordinary Org, visible in any agenda.
//
// Dates are inactive timestamps ([2026-08-04 Tue]) throughout: this is a record
// of what happened, and none of it belongs in tomorrow's agenda.
//
// Read and written by line-level text surgery rather than a parseOrg/exportOrg
// round trip, for the same reason as lib/contract_org.js: the file is the user's,
// and pushing it through the exporter could reformat content this node never
// touched. Surgery replaces the tagged node's *entire* subtree and leaves
// everything outside it alone - which also means nothing should be hand-added
// inside the node, since the next save regenerates it wholesale.

import { CHANGE_TRACK, normalizeTeamState } from './team';

export const TEAM_TAG = 'team-adoption';
export const TEAM_HEADLINE = 'Team';

// The middle change-track state, as a tag (see the header comment).
export const ACTIVE_TAG = 'active';

export const QUICK_WINS_TITLE = 'Quick wins';
export const REVIEW_TITLE = 'Review gate';
export const PULSE_TITLE = 'Pulse';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TRACK_KEYS = CHANGE_TRACK.map((ingredient) => ingredient.key);

/** A day key as an inactive org timestamp: 2026-08-04 -> [2026-08-04 Tue]. */
export const formatOrgDay = (key) => {
  const [year, month, day] = `${key}`.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(year, month - 1, day).getDay()];

  return `[${key} ${weekday}]`;
};

// A leading date in either timestamp form, active or inactive - only the day is
// ever read back out, since nothing here records a time of day.
const LEADING_DAY_REGEX = /^[[<](\d{4}-\d{2}-\d{2})[^\]>]*[\]>]\s*/;

const headlineLevel = (line) => {
  const match = /^(\*+)\s/.exec(line);
  return match ? match[1].length : null;
};

const headlineTags = (line) => {
  const match = /:((?:[^\s:]+:)+)\s*$/.exec(line);
  return match ? match[1].split(':').filter(Boolean) : [];
};

/** A headline's own text: stars, keyword and trailing tags all removed. */
const headlineTitle = (line) =>
  `${line}`
    .replace(/^\*+\s+/, '')
    .replace(/^(?:TODO|DONE)\s+/, '')
    .replace(/:(?:[^\s:]+:)+\s*$/, '')
    .trim();

// Exactly one star and the team tag: the user asked for the main node, and a
// deeper or differently-tagged headline of the same title is somebody else's.
const isTeamHeadline = (line) =>
  headlineLevel(line) === 1 && headlineTags(line).some((tag) => tag.toLowerCase() === TEAM_TAG);

/** Index one past the last line belonging to the subtree that starts at `index`. */
const endOfSubtreeIndex = (lines, index) => {
  let end = index + 1;
  while (end < lines.length) {
    const level = headlineLevel(lines[end]);
    if (level !== null && level <= 1) break;
    end += 1;
  }

  // A blank-line separator right before the next headline (or EOF) is spacing
  // between subtrees, not part of this one - excluding it from what gets
  // replaced means resaving does not eat that spacing line by line.
  while (end > index + 1 && lines[end - 1] === '') end -= 1;

  return end;
};

// A body line that starts with a star would read back as a headline and cut the
// subtree in half. Org's own escape for that is a leading comma.
const escapeBodyLine = (text) => (/^\s*\*/.test(text) ? `,${text}` : text);

const formatScore = (score) => `${Math.round(score * 100) / 100}`;

const buildNode = (state) => {
  const { track, quickWins, reviews, pulses } = normalizeTeamState(state);
  const lines = [`* ${TEAM_HEADLINE} :${TEAM_TAG}:`];

  track.forEach((entry) => {
    const keyword = entry.state === 'anchored' ? 'DONE' : 'TODO';
    const tags = entry.state === 'active' ? `${entry.key}:${ACTIVE_TAG}` : entry.key;

    lines.push(`** ${keyword} ${entry.label} :${tags}:`);
    if (entry.note) lines.push(escapeBodyLine(entry.note));
  });

  // The three logs keep their headline even while empty: the node is a form the
  // page fills in, and a section that vanishes when empty reads like a bug.
  lines.push(`** ${QUICK_WINS_TITLE}`);
  quickWins.forEach((win) => lines.push(`- ${formatOrgDay(win.date)} ${win.text}`));

  lines.push(`** ${REVIEW_TITLE}`);
  reviews.forEach((review) => {
    lines.push(`- ${formatOrgDay(review.date)} ${review.change}`);
    if (review.verified) lines.push(`  - verified :: ${review.verified}`);
    if (review.decision) lines.push(`  - decision :: ${review.decision}`);
  });

  lines.push(`** ${PULSE_TITLE}`);
  pulses.forEach((pulse) =>
    lines.push(`- ${formatOrgDay(pulse.date)} ${formatScore(pulse.score)}`)
  );

  return `${lines.join('\n')}\n`;
};

/**
 * The file contents with the team record written as the team-tagged node -
 * appended when the file has none yet, and the whole file is created when there
 * is no file at all (contents null).
 *
 * The node's entire subtree is replaced. Nothing outside it is touched.
 */
export const upsertTeamNode = (contents, state) => {
  const node = buildNode(state);

  if (contents === null || contents === undefined || contents === '') {
    return node;
  }

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isTeamHeadline(line));

  if (headlineIndex === -1) {
    const terminated = contents.endsWith('\n') ? contents : `${contents}\n`;
    return `${terminated}\n${node}`;
  }

  const end = endOfSubtreeIndex(lines, headlineIndex);
  const nodeLines = node.split('\n');
  nodeLines.pop(); // the trailing '' left by node's closing newline

  return [...lines.slice(0, headlineIndex), ...nodeLines, ...lines.slice(end)].join('\n');
};

const LIST_ITEM_REGEX = /^-\s+(.*)$/;
const SUB_ITEM_REGEX = /^\s+-\s+(verified|decision)\s*::\s*(.*)$/;

const dayAndRest = (text) => {
  const match = LEADING_DAY_REGEX.exec(text);
  if (!match) return null;

  return { date: match[1], rest: text.slice(match[0].length).trim() };
};

/**
 * The team record out of its file, in the shape lib/team.js works on, or null
 * when the file has no team-tagged node at all (so the caller can tell "no
 * record yet" from "a record with nothing in it").
 *
 * Everything unrecognised inside the node is ignored rather than guessed at:
 * the file is hand-editable and one mangled line must not cost the rest.
 */
export const extractTeamFromFile = (contents) => {
  if (!contents) return null;

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isTeamHeadline(line));
  if (headlineIndex === -1) return null;

  const end = endOfSubtreeIndex(lines, headlineIndex);

  const track = [];
  const quickWins = [];
  const reviews = [];
  const pulses = [];

  // Which level-2 subtree the lines currently being read belong to. A track line
  // is its own section: the next non-empty body line under it is its note.
  let section = null;
  let currentTrack = null;

  for (let index = headlineIndex + 1; index < end; index += 1) {
    const line = lines[index];
    const level = headlineLevel(line);

    if (level !== null) {
      const tags = headlineTags(line).map((tag) => tag.toLowerCase());
      const trackKey = tags.find((tag) => TRACK_KEYS.includes(tag));

      if (trackKey) {
        const done = /^\*+\s+DONE\s/.test(line);
        currentTrack = {
          key: trackKey,
          state: done ? 'anchored' : tags.includes(ACTIVE_TAG) ? 'active' : 'notStarted',
          note: '',
        };
        track.push(currentTrack);
        section = 'track';
        continue;
      }

      currentTrack = null;
      const title = headlineTitle(line).toLowerCase();
      section =
        title === QUICK_WINS_TITLE.toLowerCase()
          ? 'quickWins'
          : title === REVIEW_TITLE.toLowerCase()
          ? 'reviews'
          : title === PULSE_TITLE.toLowerCase()
          ? 'pulses'
          : null;
      continue;
    }

    if (!line.trim()) continue;

    if (section === 'track') {
      // One free-text line per track line (concept §4.1) - the first one wins,
      // and a comma-escaped leading star is unescaped on the way back in.
      if (currentTrack && !currentTrack.note) {
        currentTrack.note = line.replace(/^,(\s*\*)/, '$1').trim();
      }
      continue;
    }

    if (section === 'reviews') {
      const subMatch = SUB_ITEM_REGEX.exec(line);
      if (subMatch && reviews.length) {
        const last = reviews[reviews.length - 1];
        if (subMatch[1] === 'verified') last.verified = subMatch[2].trim();
        else last.decision = subMatch[2].trim().toLowerCase();
        continue;
      }
    }

    const itemMatch = LIST_ITEM_REGEX.exec(line);
    if (!itemMatch) continue;

    const dated = dayAndRest(itemMatch[1]);
    if (!dated) continue;

    if (section === 'quickWins') {
      quickWins.push({ date: dated.date, text: dated.rest });
    } else if (section === 'reviews') {
      reviews.push({ date: dated.date, change: dated.rest, verified: '', decision: null });
    } else if (section === 'pulses') {
      pulses.push({ date: dated.date, score: Number(dated.rest.split(/\s+/)[0]) });
    }
  }

  return normalizeTeamState({ track, quickWins, reviews, pulses });
};
